import React, { useState, useEffect, useMemo, useRef } from 'react';
import './App.css';
import { backendUrl, siteUrl } from './config_prod';
import { Lang } from './type/language';
import { labels } from './languages/labels-form';
import { getQueryParam } from './utils/get-query-params';
import { resolveLogoUrl } from './utils/urls';
import { useOffer } from './hooks/useOffer';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { useForm } from './hooks/useForm';
import { inscriptionServices } from './services/inscriptionService';
import {
  confirmPendingApplication,
  getAccountVerificationStatus,
  requestAccountVerificationCode,
  verifyAccountCode,
} from './services/accountVerificationService';
import {
  getSessionId,
  getVisitorId,
  readAttributionParams,
  trackATSAnalyticsEvent,
} from './services/analyticsService';
import { Header } from './components/Header';
import { OfferLine } from './components/OfferLine';
import { ApplicationForm } from './components/ApplicationForm';
import { SuccessCard } from './components/SuccessCard';
import { MissingOfferCard } from './components/MissingOfferCard';
import { FormLanguage } from './type/form-step';

function normalizeWorkType(value: unknown): string {
  const raw = String(value || '').trim();
  return raw === 'none' ? 'non' : raw;
}

function normalizeSchoolLevel(value: unknown): string {
  const raw = String(value || '').trim();
  const lower = raw.toLowerCase();
  if (!raw || raw === '_') return '';
  if (lower.includes('doctor') || lower.includes('phd')) return 'DOCTORAT';
  if (lower.includes('4') || lower.includes('5') || lower.includes('master')) return 'BAC+4_5';
  if (lower.includes('2') || lower.includes('3') || lower.includes('bachelor')) return 'BAC+2_3';
  if (lower.includes('bac')) return 'BAC';
  return raw;
}

function getOfferLanguageCode(value: unknown): string {
  if (Array.isArray(value)) {
    const firstLanguage = value.find((language) => {
      if (!language || typeof language !== 'object') return false;
      return Boolean((language as { code?: unknown }).code);
    }) as { code?: unknown } | undefined;
    return String(firstLanguage?.code || '').trim().toLowerCase();
  }
  return '';
}

function normalizeLanguageLevel(value: unknown): string {
  const level = String(value || '').trim().toLowerCase();
  if (['native', 'fluent', 'intermediate', 'basic'].includes(level)) return level;
  if (['c2', 'c1'].includes(level)) return 'fluent';
  if (['b2', 'b1'].includes(level)) return 'intermediate';
  if (['a2', 'a1'].includes(level)) return 'basic';
  return 'fluent';
}

function buildOfferLanguage(value: unknown, fallbackCode: string): FormLanguage | null {
  if (Array.isArray(value)) {
    const firstLanguage = value.find((language) => {
      if (!language || typeof language !== 'object') return false;
      return Boolean((language as { code?: unknown }).code);
    }) as { code?: unknown; name_en?: unknown; name_fr?: unknown; level?: unknown } | undefined;
    if (firstLanguage?.code) {
      return {
        code: String(firstLanguage.code).trim().toLowerCase(),
        name_en: String(firstLanguage.name_en || firstLanguage.code).trim(),
        name_fr: String(firstLanguage.name_fr || firstLanguage.name_en || firstLanguage.code).trim(),
        level: normalizeLanguageLevel(firstLanguage.level),
      };
    }
  }

  if (!fallbackCode) return null;
  const names: Record<string, { name_en: string; name_fr: string }> = {
    fr: { name_en: 'French', name_fr: 'Français' },
    en: { name_en: 'English', name_fr: 'Anglais' },
    es: { name_en: 'Spanish', name_fr: 'Espagnol' },
    de: { name_en: 'German', name_fr: 'Allemand' },
    it: { name_en: 'Italian', name_fr: 'Italien' },
  };
  const languageNames = names[fallbackCode] || {
    name_en: fallbackCode.toUpperCase(),
    name_fr: fallbackCode.toUpperCase(),
  };
  return { code: fallbackCode, ...languageNames, level: 'fluent' };
}

function App() {
  // Favicon K‑SCAN dans l'onglet du navigateur
  useEffect(() => {
    const setFavicon = (href: string) => {
      let linkEl = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.rel = 'icon';
        document.head.appendChild(linkEl);
      }
      linkEl.type = 'image/x-icon';
      linkEl.href = href;
    };
    // Utilise le fichier livré dans public/icon.ico (logo K‑SCAN)
    setFavicon('/icon.ico');
  }, []);
  // Langue initiale depuis l'URL (?lang=fr|en), défaut: fr
  const initialLang: Lang = ((getQueryParam('lang') as Lang) || 'fr') === 'en' ? 'en' : 'fr';
  const attribution = useMemo(() => readAttributionParams(), []);
  // Infos d'offre: id, titre et URLs associées
  const { offerId, offerTitle, offerUrl, formUrl, offerPrefill, companyLogoUrl: initialCompanyLogoUrl } = useOffer();
  const [displayOfferTitle, setDisplayOfferTitle] = useState(offerTitle || offerPrefill.role);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(initialCompanyLogoUrl || null);

  // Etat formulaire + erreurs via hook dédié
  const { applyResumePrefill, form, errors, update, patch, validate, setLang, setErrors } = useForm(initialLang, attribution.source, offerPrefill);
  const formStartedTrackedRef = useRef(false);
  const cvUploadedTrackedRef = useRef(false);
  const jobViewTrackedRef = useRef<string | null>(null);
  const verificationSubmissionStartedRef = useRef(false);
  const offerPrefillLoadedRef = useRef<string | null>(null);
  const formRef = useRef(form);

  // Statuts de soumission
  const [submitted, setSubmitted] = useState(false);
  const [applicationConfirmed, setApplicationConfirmed] = useState(false);
  const [applicationSending, setApplicationSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);
  const [verificationResending, setVerificationResending] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationVerifying, setVerificationVerifying] = useState(false);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [verificationExpiresAt, setVerificationExpiresAt] = useState<number | null>(null);
  const [verificationPolling, setVerificationPolling] = useState(false);
  const [now, setNow] = useState(Date.now());

  const isConfirmPage = window.location.pathname.replace(/\/+$/, '').endsWith('/confirm');
  const confirmToken = getQueryParam('token');
  const confirmEventId = getQueryParam('eventId') || getQueryParam('event_id');
  const confirmEmail = getQueryParam('email');
  const confirmOfferId = getQueryParam('jobId') || getQueryParam('opportunity_id') || offerId;
  const [confirmationStatus, setConfirmationStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [confirmationError, setConfirmationError] = useState<string | null>(null);

  function trackEvent(event: Parameters<typeof trackATSAnalyticsEvent>[0]['event'], metadata: Record<string, unknown> = {}) {
    if (!offerId) return;
    void trackATSAnalyticsEvent({
      event,
      jobId: offerId,
      attribution,
      metadata,
    });
  }

  function updateWithAnalytics<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    if (!formStartedTrackedRef.current) {
      formStartedTrackedRef.current = true;
      trackEvent('form_started', { first_field: String(key) });
    }
    if (key === 'resume' && value && !cvUploadedTrackedRef.current) {
      cvUploadedTrackedRef.current = true;
      const file = value as File;
      trackEvent('cv_uploaded', {
        size: file.size,
        type: file.type,
      });
    }
    update(key, value);
  }

  // Libellés selon la langue
  const t = labels[form.lang];

  // Titre de l'onglet
  useDocumentTitle(displayOfferTitle, t.pageTitle);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    if (!offerId) return;
    if (offerPrefillLoadedRef.current === offerId) return;
    const currentOfferId = offerId;

    let cancelled = false;
    async function prefillFormFromOffer() {
      try {
        const response = await fetch(`${backendUrl}/kscan/v1/ats/post/opportunity/${encodeURIComponent(currentOfferId)}/prefill`);
        const payload = await response.json().catch(() => null);
        if (cancelled || payload?.status !== 200 || !payload?.data) return;
        offerPrefillLoadedRef.current = currentOfferId;
        const informal = payload.data.informal_information || {};
        const localisation = payload.data.localisation || {};
        const opportunityInformation = payload.data.opportunity_information || {};
        const companyInformation = payload.data.company_information || {};
        const nextCompanyLogoUrl = resolveLogoUrl(
          companyInformation.logo_url ||
            companyInformation.logo ||
            payload.data.company_logo_url ||
            payload.data.logo_url ||
            payload.data.logo,
        );
        if (nextCompanyLogoUrl) setCompanyLogoUrl(nextCompanyLogoUrl);
        const offerRole = opportunityInformation.title || informal.profession || offerTitle || '';
        if (offerRole) setDisplayOfferTitle(offerRole);
        const currentForm = formRef.current;
        const prefillValues: Partial<typeof form> = {};
        if (!currentForm.role && offerRole) prefillValues.role = String(offerRole);
        if (!currentForm.schoolLevel && informal.degree) prefillValues.schoolLevel = normalizeSchoolLevel(informal.degree);
        if (!currentForm.experienceMonths && informal.months_of_experience !== undefined) prefillValues.experienceMonths = String(informal.months_of_experience);
        const offerLanguageCode = String(payload.data.language_used || '').trim().toLowerCase() || getOfferLanguageCode(informal.Languages);
        if (currentForm.languages.length === 0) {
          const offerLanguage = buildOfferLanguage(informal.Languages, offerLanguageCode);
          if (offerLanguage) prefillValues.languages = [offerLanguage];
        }
        if (!currentForm.languageCode && offerLanguageCode) prefillValues.languageCode = offerLanguageCode;
        if (!currentForm.availableDate) prefillValues.availableDate = 'NOW';
        if (!currentForm.city && localisation.city) prefillValues.city = String(localisation.city);
        if (currentForm.workType.length === 0 && informal.remote_work) prefillValues.workType = [normalizeWorkType(informal.remote_work)];
        if (currentForm.contractType.length === 0 && informal.type_of_opportunity) prefillValues.contractType = [String(informal.type_of_opportunity)];
        const minPayKey = informal.type_of_opportunity === 'freelance' ? 'dailyRateMin' : 'minSalary';
        const maxPayKey = informal.type_of_opportunity === 'freelance' ? 'dailyRateMax' : 'maxSalary';
        if (!currentForm[minPayKey] && informal.salary_min !== undefined) prefillValues[minPayKey] = String(informal.salary_min);
        if (!currentForm[maxPayKey] && informal.salary_max !== undefined) prefillValues[maxPayKey] = String(informal.salary_max);
        if (Object.keys(prefillValues).length > 0) {
          patch(prefillValues);
        }
      } catch {
        // Le backend dérive aussi ces valeurs à la soumission, donc l'échec du pré-remplissage UI n'est pas bloquant.
      }
    }

    void prefillFormFromOffer();
    return () => {
      cancelled = true;
    };
  }, [
    form.availableDate,
    form.city,
    form.contractType.length,
    form.experienceMonths,
    form.languageCode,
    form.languages.length,
    form.maxSalary,
    form.minSalary,
    form.role,
    form.schoolLevel,
    form.workType.length,
    offerId,
    offerTitle,
    patch,
    update,
  ]);

  useEffect(() => {
    if (!offerId) return;
    if (jobViewTrackedRef.current === offerId) return;
    jobViewTrackedRef.current = offerId;
    getVisitorId();
    getSessionId();
    trackEvent('job_view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  // URLs annexes
  const exploreUrl = siteUrl + '/jobs';
  const isVerifiedForCurrentEmail = Boolean(verificationToken && verifiedEmail === form.email.trim().toLowerCase());
  const isVerificationCooldownActive = Boolean(
    verificationRequired &&
    !isVerifiedForCurrentEmail &&
    verificationExpiresAt &&
    verificationExpiresAt > now
  );

  useEffect(() => {
    setVerificationRequired(false);
    setVerificationMessage(null);
    setVerificationToken(null);
    setVerifiedEmail(null);
    setVerificationExpiresAt(null);
    setVerificationCode('');
    setApplicationConfirmed(false);
    setApplicationSending(false);
    verificationSubmissionStartedRef.current = false;
  }, [form.email, setErrors]);

  useEffect(() => {
    if (!verificationExpiresAt || verificationExpiresAt <= Date.now()) return;

    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [verificationExpiresAt]);

  useEffect(() => {
    if (!offerId || !verificationRequired || isVerifiedForCurrentEmail || submitted) return;

    let cancelled = false;
    const email = form.email.trim().toLowerCase();

    async function pollVerificationStatus() {
      if (!offerId || cancelled) return;
      setVerificationPolling(true);
      try {
        const status = await getAccountVerificationStatus({ email, offerId });
        if (cancelled) return;
        if (status.expired) {
          setSubmitError(t.verifyAccountExpired);
          setVerificationMessage(t.verifyAccountExpired);
          setVerificationPolling(false);
          return;
        }
        if (status.verified && status.verificationToken && !verificationSubmissionStartedRef.current) {
          verificationSubmissionStartedRef.current = true;
          setVerificationToken(status.verificationToken);
          setVerifiedEmail(email);
          setVerificationExpiresAt(null);
          setVerificationMessage(t.verifyAccountSuccess);
          setSubmitting(true);
          try {
            const result = await inscriptionServices({
              offerId,
              offerTitle,
              form,
              verificationToken: status.verificationToken,
              attribution,
              visitorId: getVisitorId(),
              sessionId: getSessionId(),
            });
            if (result.submitted) {
              trackEvent('application_submitted', {
                step: 'account_verification_status',
              });
              setSubmitted(true);
            }
          } catch (err: any) {
            verificationSubmissionStartedRef.current = false;
            setSubmitError(err?.message || 'Submit error');
            trackEvent('application_failed', {
              step: 'account_verification_submit',
              message: err?.message || 'Submit error',
            });
          } finally {
            setSubmitting(false);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          trackEvent('application_failed', {
            step: 'account_verification_status',
            message: err?.message || 'Submit error',
          });
        }
      } finally {
        if (!cancelled) setVerificationPolling(false);
      }
    }

    void pollVerificationStatus();
    const intervalId = window.setInterval(() => {
      void pollVerificationStatus();
    }, 2500);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, verificationRequired, isVerifiedForCurrentEmail, submitted, form.email]);

  useEffect(() => {
    if (!isConfirmPage) return;
    if (!confirmEmail || !confirmOfferId || !confirmToken || !confirmEventId) {
      setConfirmationStatus('error');
      setConfirmationError(t.verifyAccountInvalidCode);
      return;
    }

    let cancelled = false;
    setConfirmationStatus('loading');
    setConfirmationError(null);
    const emailToConfirm = confirmEmail.trim().toLowerCase();
    const offerToConfirm = confirmOfferId;
    const tokenToConfirm = confirmToken;
    const eventToConfirm = confirmEventId;

    async function confirm() {
      try {
        await confirmPendingApplication({
          email: emailToConfirm,
          offerId: offerToConfirm,
          token: tokenToConfirm,
          eventId: eventToConfirm,
          lang: form.lang,
        });
        void trackATSAnalyticsEvent({
          event: 'application_submitted',
          jobId: offerToConfirm,
          attribution,
          metadata: {
            step: 'account_verification_confirm_link',
            event_id: eventToConfirm,
          },
        });
        if (!cancelled) setConfirmationStatus('success');
      } catch (err: any) {
        if (!cancelled) {
          setConfirmationStatus('error');
          setConfirmationError(err?.message || t.verifyAccountInvalidCode);
        }
      }
    }

    void confirm();
    return () => {
      cancelled = true;
    };
  }, [
    attribution,
    confirmEmail,
    confirmEventId,
    confirmOfferId,
    confirmToken,
    form.lang,
    isConfirmPage,
    t.verifyAccountInvalidCode,
  ]);

  async function requestVerificationEmail() {
    if (!offerId) return false;

    const fullName = form.name || `${form.firstName} ${form.lastName}`.trim();
    const result = await requestAccountVerificationCode({
      email: form.email.trim().toLowerCase(),
      offerId,
      name: fullName,
      lang: form.lang,
    });
    if (!result.required) return false;

    const expiresInMinutes = result.expiresInMinutes || 15;
    const requestedAt = Date.now();
    setVerificationRequired(true);
    setNow(requestedAt);
    setVerificationExpiresAt(requestedAt + expiresInMinutes * 60 * 1000);
    setVerificationMessage(t.verifyAccountCodeSent);
    return true;
  }

  async function handleVerifyCode() {
    if (!offerId || !verificationCode.trim()) return;

    setVerificationVerifying(true);
    setSubmitError(null);
    try {
      const token = await verifyAccountCode({
        email: form.email.trim().toLowerCase(),
        offerId,
        code: verificationCode,
      });
      setVerificationToken(token);
      setVerifiedEmail(form.email.trim().toLowerCase());
      setVerificationExpiresAt(null);
      setVerificationMessage(t.verifyAccountSuccess);
      setApplicationSending(true);
      setSubmitting(true);
      const result = await inscriptionServices({
        offerId,
        offerTitle,
        form,
        verificationToken: token,
        attribution,
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
      });
      if (result.submitted) {
        trackEvent('application_submitted', {
          step: 'account_verification_code',
        });
        setApplicationConfirmed(true);
      } else {
        throw new Error(t.verifyAccountRequired);
      }
    } catch (err: any) {
      setApplicationSending(false);
      setVerificationMessage(err?.message || t.verifyAccountInvalidCode);
      setSubmitError(err?.message || t.verifyAccountInvalidCode);
      trackEvent('application_failed', {
        step: 'account_verification_code',
        message: err?.message || 'Invalid verification code',
      });
    } finally {
      setVerificationVerifying(false);
      setSubmitting(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setErrors({});
    // Validation locale
    if (!validate(t)) {
      trackEvent('application_failed', { step: 'validation' });
      return;
    }
    if (!offerId) {
      setSubmitError('Missing offer id in URL.');
      return;
    }

    if (isVerificationCooldownActive) {
      return;
    }

    if (!isVerifiedForCurrentEmail) {
      setVerificationRequired(true);
      setVerificationMessage(t.verifyAccountRequired);
      return;
    }

    setSubmitting(true);
    try {
      const result = await inscriptionServices({
        offerId,
        offerTitle,
        form,
        verificationToken,
        attribution,
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
      });
      if (result.verificationRequired) {
        const expiresInMinutes = result.expiresInMinutes || 15;
        const requestedAt = Date.now();
        setVerificationRequired(true);
        setNow(requestedAt);
        setVerificationExpiresAt(requestedAt + expiresInMinutes * 60 * 1000);
        setVerificationMessage(t.verifyAccountMailSent);
        return;
      }
      trackEvent('application_submitted');
      setSubmitted(true);
    } catch (err: any) {
      trackEvent('application_failed', {
        step: 'submission',
        message: err?.message || 'Submit error',
      });
      setSubmitError(err?.message || 'Submit error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendCode() {
    if (!offerId) return;

    setVerificationResending(true);
    setSubmitError(null);
    setVerificationToken(null);
    setVerifiedEmail(null);
    setVerificationCode('');
    verificationSubmissionStartedRef.current = false;

    try {
      await requestVerificationEmail();
    } catch (err: any) {
      trackEvent('application_failed', {
        step: 'account_verification_resend',
        message: err?.message || 'Submit error',
      });
      setSubmitError(err?.message || 'Submit error');
    } finally {
      setVerificationResending(false);
    }
  }

  if (isConfirmPage) {
    return (
      <div className="background-neon">
        <div className="app-container">
          <Header siteUrl={siteUrl} lang={form.lang} onChangeLang={setLang} t={t} />
          <div className={`card verification-panel${confirmationStatus === 'success' ? ' success' : ''}`}>
            <div className="verification-status">
              {confirmationStatus === 'loading' && <span className="verification-spinner" aria-hidden="true" />}
              <div>
                <div className="verification-title">
                  {confirmationStatus === 'success' ? t.verifyAccountValidated : t.verifyAccountTitle}
                </div>
                <div className="verification-text">
                  {confirmationStatus === 'success'
                    ? t.thanksTitle
                    : confirmationStatus === 'error'
                      ? confirmationError || t.verifyAccountInvalidCode
                      : t.verifyAccountSubmittingCv}
                </div>
              </div>
            </div>
            {confirmationStatus === 'success' && (
              <div className="actions-row">
                <a className="button primary" href={exploreUrl}>
                  {t.exploreOffers}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Ecran de succès
  if (submitted) {
    return (
      <div className="background-neon">
        <div className="app-container">
          <Header siteUrl={siteUrl} lang={form.lang} onChangeLang={setLang} t={t} />
          <SuccessCard siteUrl={siteUrl} exploreUrl={exploreUrl} t={t} />
        </div>
      </div>
    );
  }

  // Ecran principal
  return (
    <div className="background-neon">
      <div className="app-container">
        <Header siteUrl={siteUrl} lang={form.lang} onChangeLang={setLang} t={t} />

        {offerId ? (
          <div className="card">
            <OfferLine title={displayOfferTitle} offerId={offerId} offerUrl={offerUrl} companyLogoUrl={companyLogoUrl} t={t} />
            <ApplicationForm
              form={form}
              errors={errors}
              setErrors={setErrors}
              update={updateWithAnalytics}
              onSubmit={onSubmit}
              submitting={submitting}
              opportunityId={offerId || ""}
              onPrefill={applyResumePrefill}
              verificationRequired={verificationRequired}
              verificationMessage={verificationMessage}
              verificationSuccess={isVerifiedForCurrentEmail}
              verificationPolling={verificationPolling}
              verificationResending={verificationResending}
              verificationCode={verificationCode}
              verificationVerifying={verificationVerifying}
              applicationConfirmed={applicationConfirmed}
              applicationSending={applicationSending}
              onVerificationCodeChange={(value: string) => setVerificationCode(value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
              onVerifyCode={handleVerifyCode}
              submitDisabled={isVerificationCooldownActive}
              onResendCode={handleResendCode}
              t={t}
              debugFormUrl={formUrl ? `URL formulaire: ${formUrl}` : null}
            />
            {submitError && <div className="error" style={{ marginTop: 8 }}>{submitError}</div>}
          </div>
        ) : (
          // Amélioration UX: afficher une aide si jobId absent
          <MissingOfferCard t={t} siteUrl={siteUrl} />
        )}

      </div>
    </div>
  );
}

export default App;
